import { identityService, Identity, IdentitySettings, Alias, Credential } from '../../../services/identity.ts';
import { ToastService } from '../../../services/toast.ts';
import { MetricsService } from '../../../services/metrics.ts';
import { OrganizationService } from '../../../services/organization.ts';
import { SubNav } from '../../../components/library/ui/sub-nav.ts';
import '../../../components/library/ui/app-button.ts';
import '../../../components/library/ui/app-modal.ts';
import '../../../components/library/ui/app-icon.ts';
import { AppSaveBar } from '../../../components/library/ui/app-save-bar.ts';
import '../../../components/library/ui/charts/app-chart-explorer.ts';
import '../../../components/library/ui/app-time-picker.ts';

export class AppIdentity extends HTMLElement {
  private _orgId: string = '';
  private _tab: string = 'metrics';
  private _timeRange: string = '24h';
  private _customRange: any = (() => { try { return JSON.parse(localStorage.getItem('crate_custom_range') || '{}') } catch { return {} } })();
  
  private _identities: Identity[] = [];
  private _settings: IdentitySettings | null = null;
  private _selectedIdentity: Identity | null = null;
  private _selectedAliases: Alias[] = [];
  private _selectedCredentials: Credential[] = [];
  private _isLoading: boolean = false;
  private _isInitialLoad: boolean = true;
  private _hasAccess: boolean = false;
  private _lastFetchTime = 0;

  private _orgService = OrganizationService.getInstance();
  private _metricsService = MetricsService.getInstance();
  private _metrics = {
      logins: 0,
      uniqueLogins: 0,
      registrations: 0,
  };

  static get observedAttributes() {
    return ['org-id', 'tab'];
  }

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    
    if (name === 'org-id') {
      this._orgId = newValue;
      if (this.isConnected) {
        this.loadData();
      }
    } else if (name === 'tab') {
      this._tab = newValue || 'users';
      if (this.isConnected) {
        this.loadData();
        this.emitPanel(); // Fix: Update the subnav highlighting
      }
    }
  }

  async connectedCallback() {
    if (!this._orgId) {
      this._orgId = this.getAttribute('org-id') || '';
    }
    if (!this.getAttribute('tab')) {
      this._tab = 'metrics';
    }
    this.render();
    if (this._orgId) {
      await this.loadData();
    }
    requestAnimationFrame(() => this.emitPanel());
  }

  private _loadTimeout: number | null = null;

  private loadData(): Promise<void> {
    if (!this._orgId) return Promise.resolve();

    return new Promise((resolve) => {
      if (this._loadTimeout !== null) {
        window.clearTimeout(this._loadTimeout);
      }
      
      this._loadTimeout = window.setTimeout(async () => {
        this._loadTimeout = null;
        this._isLoading = true;
        this.render();

        try {
          const subscription = await this._orgService.getSubscription(this._orgId);
          const modules = subscription?.modules || [];
          this._hasAccess = modules.some((m: any) => {
            const id = typeof m === 'object' ? m.id : m;
            return id === 'identity';
          });

          if (!this._hasAccess) {
              return;
          }

          if (this._tab === 'users') {
            this._identities = await identityService.listIdentities(this._orgId) || [];
          } else if (this._tab === 'settings') {
            this._settings = await identityService.getSettings(this._orgId);
          } else if (this._tab === 'metrics') {
            try {
              let start: number | undefined;
              let end: number | undefined;
              if (this._timeRange === 'custom' && this._customRange.start && this._customRange.end) {
                  start = Math.floor(new Date(this._customRange.start).getTime() / 1000);
                  end = Math.floor(new Date(this._customRange.end).getTime() / 1000);
              }
              const identityLogins = await this._metricsService.getMetric('traffic', this._orgId, this._timeRange, start, end, { path: '/identity/login/finish', method: 'POST', status: '2xx' });
              const uniqueLogins = await this._metricsService.getMetric('unique_identities', this._orgId, this._timeRange, start, end, { path: '/identity/login/finish', method: 'POST', status: '2xx' });
              const identityRegs = await this._metricsService.getMetric('traffic', this._orgId, this._timeRange, start, end, { path: '/identity/register/finish', method: 'POST', status: '2xx' });
              
              this._metrics.logins = identityLogins?.value || identityLogins?.traffic || 0;
              this._metrics.uniqueLogins = uniqueLogins?.value || 0;
              this._metrics.registrations = identityRegs?.value || identityRegs?.traffic || 0;
            } catch (e) {
              console.warn("Metrics failed to load", e);
            }
          }
        } catch (error) {
          console.error(error);
        } finally {
          this._isLoading = false;
          this._isInitialLoad = false;
          this.render();
          resolve();
        }
      }, 20);
    });
  }

  private emitPanel() {
    const views = [
      { id: 'metrics', name: 'Metrics', icon: 'bar-chart' },
      { id: 'users', name: 'Users', icon: 'users' },
      { id: 'settings', name: 'Settings', icon: 'settings' }
    ];

    this._subNav = document.createElement('app-sub-nav') as SubNav;
    this._subNav.title = 'Identity';
    this._subNav.items = views;
    this._subNav.activeId = this._tab;

    this._subNav.addEventListener('nav-change', ((e: CustomEvent) => {
      const tab = e.detail.id;
      window.dispatchEvent(new CustomEvent('navigate', { 
        detail: { view: 'identity', params: { tab } } 
      }));
    }) as any);

    const viewMeta: Record<string, { title: string, subtitle: string }> = {
      'metrics': { title: 'Identity', subtitle: 'Metrics' },
      'users': { title: 'Identity', subtitle: 'Users' },
      'settings': { title: 'Identity', subtitle: 'Settings' }
    };

    const meta = viewMeta[this._tab] || { title: 'Identity', subtitle: '' };

    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      detail: { 
        panel: this._subNav,
        header: meta
      }, 
      bubbles: true, 
      composed: true 
    }));
  }

  private async selectIdentity(identity: Identity) {
    this._selectedIdentity = identity;
    this.render(); 
    
    try {
        this._selectedAliases = await identityService.listAliases(this._orgId, identity.id) || [];
    } catch(e) { this._selectedAliases = []; }
    
    try {
        this._selectedCredentials = await identityService.listCredentials(this._orgId, identity.id) || [];
    } catch(e) { this._selectedCredentials = []; }
    
    this.updateUserPanel();
  }

  private updateUserPanel() {
      const modal = this.querySelector('#user-details-modal') as HTMLElement;
      if (!modal) return;

      if (!this._selectedIdentity) {
          modal.removeAttribute('open');
          return;
      }

      modal.innerHTML = `
        <span slot="header" style="text-transform: uppercase; color: var(--accent);">${this._selectedIdentity.display_name || 'User Details'}</span>
        <div style="padding: 24px 0; font-family: var(--font-mono);">
            <div style="margin-bottom: 24px;">
                <h4 style="color: var(--text-dim); margin: 0 0 12px 0; font-size: 0.85rem; text-transform: uppercase;">Aliases</h4>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                    ${this._selectedAliases.map(a => `
                        <div style="background: rgba(255,255,255,0.02); padding: 12px; border: 1px solid var(--border-sketch); border-radius: 4px;">
                            <div style="color: var(--text-primary); font-size: 0.9rem;">${a.alias}</div>
                            <div style="color: var(--text-dim); font-size: 0.75rem; margin-top: 4px;">${a.alias_type} ${a.is_primary ? '(Primary)' : ''}</div>
                        </div>
                    `).join('') || '<div class="empty-state" style="padding: 12px;">No aliases found</div>'}
                </div>
            </div>

            <div>
                <h4 style="color: var(--text-dim); margin: 0 0 12px 0; font-size: 0.85rem; text-transform: uppercase;">Credentials</h4>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                    ${this._selectedCredentials.map(c => {
                        const dateText = c.last_used_at ? 
                            `Last used: ${new Date(c.last_used_at).toLocaleDateString()}` : 
                            `Created: ${new Date(c.created_at).toLocaleDateString()}`;
                        return `
                        <div style="background: rgba(255,255,255,0.02); padding: 12px; border: 1px solid var(--border-sketch); border-radius: 4px; display: flex; justify-content: space-between; align-items: center;">
                            <div style="display: flex; align-items: center; gap: 12px;">
                                <app-icon name="${c.type === 'password' ? 'key' : 'shield'}" size="20" style="color: var(--text-dim);"></app-icon>
                                <div>
                                    <div style="color: var(--text-primary); font-size: 0.9rem; text-transform: capitalize;">
                                        ${c.type === 'password' ? 'Password' : `Passkey - ${c.device_type || 'Unknown'}`}
                                    </div>
                                    <div style="color: var(--text-dim); font-size: 0.75rem; margin-top: 4px;">
                                        ${c.type === 'passkey' && c.rp_id ? `Site: ${c.rp_id} &middot; ` : ''}
                                        ${c.type === 'passkey' ? `Sign count: ${c.sign_count ?? 0} &middot; ` : ''}
                                        ${dateText}
                                    </div>
                                </div>
                            </div>
                            ${c.type !== 'password' ? `<app-button variant="ghost" size="sm" class="remove-cred-btn" data-id="${c.credential_id}" icon="trash" title="Remove Credential" style="color: var(--error); cursor: pointer;"></app-button>` : ''}
                        </div>
                    `}).join('') || '<div class="empty-state" style="padding: 12px;">No credentials found</div>'}
                </div>
                <div style="margin-top: 12px; font-size: 0.75rem; color: var(--text-dim); line-height: 1.4;">
                    If a user loses access to their WebAuthn device, removing the credential will allow them to re-register on their next login attempt.
                </div>
            </div>
        </div>
        <div slot="actions">
            <app-button id="close-modal-btn" variant="ghost">Close</app-button>
        </div>
      `;

      modal.querySelector('#close-modal-btn')?.addEventListener('click', () => {
          this._selectedIdentity = null;
          modal.removeAttribute('open');
      });

      modal.querySelectorAll('.remove-cred-btn').forEach(btn => {
          btn.addEventListener('click', async (e) => {
              const credId = (e.currentTarget as HTMLElement).dataset.id;
              if (credId && this._selectedIdentity) {
                  if (confirm('Are you sure you want to remove this credential? The user will need to re-register it.')) {
                      try {
                          await identityService.removeCredential(this._orgId, this._selectedIdentity.id, credId);
                          ToastService.getInstance().show('Credential removed', 'success');
                          this.selectIdentity(this._selectedIdentity);
                      } catch (err) {
                      }
                  }
              }
          });
      });

      modal.setAttribute('open', '');
  }

  private async saveSettings() {
      if (!this._settings || !this._orgId) return;
      
      const saveBar = this.querySelector('app-save-bar') as AppSaveBar;
      if (saveBar) saveBar.loading = true;

      const publicReg = (this.querySelector('#toggle-non-verified') as HTMLInputElement)?.checked;
      const magicLinks = (this.querySelector('#toggle-magic-links') as HTMLInputElement)?.checked;
      const passwords = (this.querySelector('#toggle-passwords') as HTMLInputElement)?.checked;
      
      const newSettings = {
          allow_public_registration: publicReg,
          allow_magic_links: magicLinks,
          allow_passwords: passwords
      };

      try {
          await identityService.updateSettings(this._orgId, newSettings);
          this._settings = { ...this._settings, ...newSettings };
          ToastService.getInstance().show('Settings updated', 'success');
          if (saveBar) saveBar.resetInitialState(this);
      } catch (err) {
          console.error('saveSettings error:', err);
          ToastService.getInstance().show('Failed to update settings', 'error');
      } finally {
          if (saveBar) saveBar.loading = false;
      }
  }

  render() {
      if (this._isLoading && this._isInitialLoad) {
          this.innerHTML = `
            <div style="height: calc(100vh - 120px); display: flex; align-items: center; justify-content: center;">
              <div class="loader"></div>
            </div>
            <style>
              .loader {
                width: 40px;
                height: 40px;
                border: 3px solid rgba(245, 158, 11, 0.1);
                border-top-color: var(--accent);
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            </style>
          `;
          return;
      }

      if (!this._hasAccess && !this._isLoading) {
          this.innerHTML = `
            <style>
              .sketchy-card {
                position: relative;
                background: rgba(255,255,255,0.01);
                padding: 32px;
                transition: all 0.2s ease;
                border: 1px solid var(--border, rgba(255,255,255,0.1));
                border-radius: 10px;
              }
            </style>
            <div style="height: calc(100vh - 120px); display: flex; align-items: center; justify-content: center;">
              <div class="sketchy-card" style="max-width: 480px; width: 100%; text-align: center; padding: 48px 32px;">
                <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(circle at center, rgba(245,158,11,0.03) 0%, transparent 70%); pointer-events: none;"></div>
                <div style="position: relative; z-index: 2;">
                  <app-icon name="lock" size="48" style="color: var(--text-muted, rgba(255,255,255,0.4)); margin-bottom: 20px; opacity: 0.8;"></app-icon>
                  <h1 style="font-family: var(--font-main); font-size: 1.5rem; margin-bottom: 12px; color: var(--text-main, #fff); font-weight: 500;">Module Locked</h1>
                  <p style="font-family: var(--font-main); color: var(--text-dim, #aaa); line-height: 1.5; margin-bottom: 28px; font-size: 0.95rem;">
                    Your organization does not have the Users & Identity module enabled.
                    Enable it in your organization settings to start managing your authentication flow.
                  </p>
                  <app-button variant="primary" size="sm" id="btn-enable-identity" style="width: 100%;">
                    Enable Module
                  </app-button>
                </div>
              </div>
            </div>
          `;
          this.querySelector('#btn-enable-identity')?.addEventListener('click', () => {
            window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'organization', params: { tab: 'billing' } } }));
          });
          return;
      }

      let tabContent = '';

    if (this._tab === 'metrics') {
        const totalActions = this._metrics.registrations + this._metrics.logins;
        const safeTotal = totalActions > 0 ? totalActions : 1;
        
        const regPct = (this._metrics.registrations / safeTotal) * 100;
        const uniquePct = (this._metrics.uniqueLogins / safeTotal) * 100;
        const repeatLogins = Math.max(0, this._metrics.logins - this._metrics.uniqueLogins);
        const repeatPct = (repeatLogins / safeTotal) * 100;

        // Helper to get midpoint of a segment
        const getMidPoint = (pctStart: number, pctSegment: number) => {
            if (pctSegment === 0) return { x: 50, y: 50 };
            const midPct = pctStart + (pctSegment / 2);
            const radians = (midPct / 100) * (2 * Math.PI) - (Math.PI / 2);
            return {
                x: 50 + 20 * Math.cos(radians),
                y: 50 + 20 * Math.sin(radians)
            };
        };

        // Layout engine for labels to prevent overlap and ensure lines don't cross the chart
        const labels = [
            { id: 'reg', title: 'REGISTRATIONS', color: 'white', point: getMidPoint(0, regPct), val: this._metrics.registrations },
            { id: 'unique', title: 'UNIQUE LOGINS', color: 'var(--accent)', point: getMidPoint(regPct, uniquePct), val: this._metrics.uniqueLogins },
            { id: 'repeat', title: 'REPEAT LOGINS', color: 'var(--text-dim)', point: getMidPoint(regPct + uniquePct, repeatPct), val: repeatLogins }
        ];

        let layout = labels.map(l => {
            const isLeft = l.point.x < 50;
            return { ...l, isLeft, textY: l.point.y };
        });

        // Anti-collision
        ['left', 'right'].forEach(side => {
            const isLeftSide = side === 'left';
            const sideLabels = layout.filter(l => l.isLeft === isLeftSide).sort((a,b) => a.textY - b.textY);
            
            // Push down
            for (let i = 1; i < sideLabels.length; i++) {
                if (sideLabels[i].textY - sideLabels[i-1].textY < 15) {
                    sideLabels[i].textY = sideLabels[i-1].textY + 15;
                }
            }
            // Push up
            for (let i = sideLabels.length - 2; i >= 0; i--) {
                if (sideLabels[i+1].textY - sideLabels[i].textY < 15) {
                    sideLabels[i].textY = sideLabels[i+1].textY - 15;
                }
            }
        });

        const renderLabel = (l: any) => {
            if (l.val === 0) return '';
            
            const dx = l.point.x - 50;
            const dy = l.point.y - 50;
            const len = Math.sqrt(dx*dx + dy*dy);
            const radialLength = 12; // Split the difference to hit exactly ~6 dashes
            const bumpX = l.point.x + (dx/len) * radialLength;
            const bumpY = l.point.y + (dy/len) * radialLength;
            
            const lineTargetX = l.isLeft ? 12 : 88;
            const textX = l.isLeft ? 10 : 90;
            const textAnchor = l.isLeft ? 'end' : 'start';
            
            return `
                <path d="M ${l.point.x} ${l.point.y} L ${bumpX} ${bumpY} L ${lineTargetX} ${l.textY - 1}" fill="none" stroke="${l.color}" stroke-width="0.3" stroke-dasharray="0.5 1" opacity="0.6"/>
                <text x="${textX}" y="${l.textY - 2}" text-anchor="${textAnchor}" font-family="var(--font-sketch)" font-size="6" fill="${l.color}" font-weight="300">
                    ${l.val.toLocaleString()}
                </text>
                <text x="${textX}" y="${l.textY + 2}" text-anchor="${textAnchor}" font-family="var(--font-mono)" font-size="2" fill="var(--text-dim)" letter-spacing="0.5">
                    ${l.title}
                </text>
            `;
        };

        tabContent = `
        <div class="dashboard-controls" style="display: flex; justify-content: flex-end; align-items: center; gap: 0.5rem; margin-bottom: 24px;">
          <app-button variant="ghost" size="sm" id="btn-open-explorer" icon="search" style="margin-right: auto; border: 1px solid rgba(255,255,255,0.08);">Data Explorer</app-button>
          
          <div style="position: relative; display: flex; align-items: center; gap: 0.5rem;">
            <app-time-picker value="${this._timeRange}"></app-time-picker>
            
            <div style="width: 1px; height: 20px; background: rgba(255,255,255,0.08); margin: 0 4px; align-self: center;"></div>
            <app-button variant="ghost" size="sm" id="refresh-btn" icon="rotate-cw"></app-button>
          </div>
        </div>

        <div class="technical-card sketch-border" style="background: rgba(15, 23, 42, 0.2); margin-bottom: 24px; position: relative;">
            
            <div style="width: 100%; height: 500px; display: flex; align-items: center; justify-content: center;">
                <svg width="100%" height="100%" viewBox="0 0 100 100" style="overflow: visible; display: block;">
                    <defs>
                        <filter id="sketch-filter" x="-20%" y="-20%" width="140%" height="140%">
                            <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="2" result="noise" />
                            <feDisplacementMap in="SourceGraphic" in2="noise" scale="1" xChannelSelector="R" yChannelSelector="G" />
                        </filter>
                        <pattern id="blueprint-grid" width="4" height="4" patternUnits="userSpaceOnUse">
                            <path d="M 4 0 L 0 0 0 4" fill="none" stroke="rgba(255, 255, 255, 0.03)" stroke-width="0.2"/>
                        </pattern>
                    </defs>

                    <!-- Blueprint Background Grid -->
                    <rect x="0" y="0" width="100" height="100" fill="url(#blueprint-grid)" rx="2" />
                    
                    <!-- Rings rendered first so labels draw on top (Z-index) -->
                    <g transform="translate(50, 50) rotate(-90)">
                        <!-- Base track -->
                        <circle cx="0" cy="0" r="20" fill="transparent" stroke="rgba(255,255,255,0.05)" stroke-width="4" filter="url(#sketch-filter)"></circle>
                        
                        <!-- Reg Ring -->
                        <circle cx="0" cy="0" r="20" pathLength="100" fill="transparent" stroke="${labels[0].color}" stroke-width="4" 
                                stroke-dasharray="${regPct} ${100 - regPct}" stroke-dashoffset="0" style="transition: stroke-dasharray 1s ease-out;" filter="url(#sketch-filter)"></circle>
                                
                        <!-- Unique Ring -->
                        <circle cx="0" cy="0" r="20" pathLength="100" fill="transparent" stroke="${labels[1].color}" stroke-width="4" 
                                stroke-dasharray="${uniquePct} ${100 - uniquePct}" stroke-dashoffset="${-regPct}" style="transition: stroke-dashoffset 1s ease-out, stroke-dasharray 1s ease-out;" filter="url(#sketch-filter)"></circle>

                        <!-- Repeat Ring -->
                        <circle cx="0" cy="0" r="20" pathLength="100" fill="transparent" stroke="${labels[2].color}" stroke-width="4" 
                                stroke-dasharray="${repeatPct} ${100 - repeatPct}" stroke-dashoffset="${-(regPct + uniquePct)}" style="transition: stroke-dashoffset 1s ease-out, stroke-dasharray 1s ease-out;" filter="url(#sketch-filter)"></circle>
                    </g>
                    
                    <!-- Schematic Draft Lines connecting segments to text (renders on top) -->
                    ${layout.map(l => renderLabel(l)).join('')}
                </svg>
            </div>
            
            <!-- Absolute Overlay for Total Logins (True Top Left of Container) -->
            <div style="position: absolute; top: 20px; left: 20px; z-index: 10; display: flex; flex-direction: column; align-items: center;">
                <div style="font-family: var(--font-mono); font-size: 0.85rem; color: var(--text-dim); letter-spacing: 0.5px; margin-bottom: 4px;">
                    TOTAL LOGINS
                </div>
                <div style="font-family: var(--font-sketch); font-size: 3rem; color: white; font-weight: 300; line-height: 1;">
                    ${this._metrics.logins.toLocaleString()}
                </div>
            </div>
        </div>
        `;
    } else if (this._tab === 'users') {
      tabContent = `
        <div class="technical-card sketch-border">
            <table class="data-table" style="width: 100%; border-collapse: collapse;">
                <thead>
                    <tr>
                        <th style="text-align: left; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.1); color: var(--text-dim); font-weight: 600; font-size: 0.75rem; text-transform: uppercase;">ID</th>
                        <th style="text-align: left; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.1); color: var(--text-dim); font-weight: 600; font-size: 0.75rem; text-transform: uppercase;">Display Name</th>
                        <th style="text-align: left; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.1); color: var(--text-dim); font-weight: 600; font-size: 0.75rem; text-transform: uppercase;">Created</th>
                        <th style="text-align: right; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.1); color: var(--text-dim); font-weight: 600; font-size: 0.75rem; text-transform: uppercase;">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    ${this._identities.length === 0 ? `
                        <tr><td colspan="4" style="padding: 48px; text-align: center; color: var(--text-dim);">No identities found.</td></tr>
                    ` : this._identities.map(id => `
                        <tr>
                            <td style="padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); font-size: 0.75rem; color: var(--text-dim); font-family: var(--font-mono);">${id.id}</td>
                            <td style="padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05);">${id.display_name || 'Unknown User'}</td>
                            <td style="padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); color: var(--text-dim);">${new Date(id.created_at).toLocaleDateString()}</td>
                            <td style="padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); text-align: right;">
                                <app-button variant="ghost" size="sm" class="view-identity-btn" data-id="${id.id}" icon="settings" title="Manage User"></app-button>
                            </td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        </div>
      `;
    } else if (this._tab === 'settings') {
      tabContent = `
        <style>
            .toggle-switch input:checked + .slider {
                background-color: var(--accent) !important;
            }
            .toggle-switch input:not(:checked) + .slider {
                background-color: rgba(255,255,255,0.1) !important;
            }
            .toggle-switch input:checked + .slider > span {
                transform: translateX(20px) !important;
            }
            .toggle-switch input:not(:checked) + .slider > span {
                transform: translateX(0) !important;
            }
        </style>
        <div class="technical-card sketch-border" style="padding: 32px; max-width: 800px; margin: 0 auto; background: rgba(15, 23, 42, 0.4);">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 32px; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 16px;">
                <app-icon name="shield" size="24" style="color: var(--accent);"></app-icon>
                <h3 style="font-family: var(--font-main); font-weight: 500; color: var(--text-main); margin: 0; font-size: 1.25rem;">Authentication Methods</h3>
            </div>
            
            <div class="settings-group" style="display: flex; flex-direction: column; gap: 24px;">
                <div class="setting-item" style="display: flex; justify-content: space-between; align-items: flex-start; padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; transition: background 0.2s;">
                    <div class="setting-info" style="flex: 1; padding-right: 24px;">
                        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                            <app-icon name="globe" size="16" style="color: var(--text-dim);"></app-icon>
                            <span class="setting-name" style="font-size: 1rem; font-weight: 500; color: var(--text-primary);">Allow Public Registration</span>
                        </div>
                        <span class="setting-desc" style="font-size: 0.85rem; color: var(--text-dim); line-height: 1.5; display: block;">Allow full front-end registration without out-of-band verification (e.g., email confirmation).</span>
                    </div>
                    <label class="toggle-switch" style="position: relative; display: inline-block; width: 44px; height: 24px;">
                        <input type="checkbox" id="toggle-non-verified" ${this._settings?.allow_public_registration ? 'checked' : ''} style="opacity: 0; width: 0; height: 0;">
                        <span class="slider" style="position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; transition: .4s; border-radius: 24px;">
                            <span style="position: absolute; content: ''; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></span>
                        </span>
                    </label>
                </div>

                <div class="setting-item" style="display: flex; justify-content: space-between; align-items: flex-start; padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; transition: background 0.2s;">
                    <div class="setting-info" style="flex: 1; padding-right: 24px;">
                        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                            <app-icon name="mail" size="16" style="color: var(--text-dim);"></app-icon>
                            <span class="setting-name" style="font-size: 1rem; font-weight: 500; color: var(--text-primary);">Magic Links</span>
                        </div>
                        <span class="setting-desc" style="font-size: 0.85rem; color: var(--text-dim); line-height: 1.5; display: block;">Allow users to log in via a magic link sent to their email address.</span>
                    </div>
                    <label class="toggle-switch" style="position: relative; display: inline-block; width: 44px; height: 24px;">
                        <input type="checkbox" id="toggle-magic-links" ${this._settings?.allow_magic_links ? 'checked' : ''} style="opacity: 0; width: 0; height: 0;">
                        <span class="slider" style="position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; transition: .4s; border-radius: 24px;">
                            <span style="position: absolute; content: ''; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></span>
                        </span>
                    </label>
                </div>

                <div class="setting-item" style="display: flex; justify-content: space-between; align-items: flex-start; padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; transition: background 0.2s;">
                    <div class="setting-info" style="flex: 1; padding-right: 24px;">
                        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                            <app-icon name="key" size="16" style="color: var(--text-dim);"></app-icon>
                            <span class="setting-name" style="font-size: 1rem; font-weight: 500; color: var(--text-primary);">Passwords</span>
                        </div>
                        <span class="setting-desc" style="font-size: 0.85rem; color: var(--text-dim); line-height: 1.5; display: block;">Allow users to configure and log in with a password.</span>
                    </div>
                    <label class="toggle-switch" style="position: relative; display: inline-block; width: 44px; height: 24px;">
                        <input type="checkbox" id="toggle-passwords" ${this._settings?.allow_passwords ? 'checked' : ''} style="opacity: 0; width: 0; height: 0;">
                        <span class="slider" style="position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; transition: .4s; border-radius: 24px;">
                            <span style="position: absolute; content: ''; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></span>
                        </span>
                    </label>
                </div>
            </div>
            
            <div style="margin-top: 32px; padding: 16px; background: rgba(16, 185, 129, 0.1); border-left: 4px solid var(--success); border-radius: 4px; display: flex; align-items: flex-start; gap: 12px;">
                <app-icon name="check-circle" size="20" style="color: var(--success); margin-top: 2px;"></app-icon>
                <div>
                    <div style="font-weight: 600; color: var(--success); margin-bottom: 4px; font-size: 0.9rem;">WebAuthn Supported</div>
                    <div style="font-size: 0.85rem; color: var(--text-dim); line-height: 1.5;">WebAuthn (Passkeys) is always supported as a secure primary credential type and cannot be disabled.</div>
                </div>
            </div>
        </div>
      `;
    }

    this.innerHTML = `
      <div class="gateway-container">
        <app-save-bar id="identity-save-bar"></app-save-bar>
        <app-modal id="user-details-modal" size="lg"></app-modal>
        <app-chart-explorer></app-chart-explorer>
        <div class="gateway-content">
          ${this._isLoading && !this._isInitialLoad ? `<div style="opacity: 0.5; transition: opacity 0.2s;">` : ''}
          ${tabContent}
          ${this._isLoading && !this._isInitialLoad ? `</div>` : ''}
        </div>
      </div>
    `;

    this.querySelectorAll('.view-identity-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const id = (e.currentTarget as HTMLElement).dataset.id;
            const identity = this._identities.find(i => i.id === id);
            if (identity) {
                this.selectIdentity(identity);
            }
        });
    });

    this.querySelector('#btn-open-explorer')?.addEventListener('click', () => {
        const explorer = this.querySelector('app-chart-explorer') as any;
        if (explorer) {
            explorer.open('traffic', this._orgId, this._timeRange, this._customRange, undefined, undefined, {}, { path: '/identity' });
        }
    });

    this.querySelector('app-time-picker')?.addEventListener('range-change', (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail.range === 'custom' && detail.customRange) {
            this._customRange = detail.customRange;
        }
        if (detail.range && detail.range !== this._timeRange) {
            this._timeRange = detail.range;
            this.loadData();
        }
    });

    this.querySelector('#refresh-btn')?.addEventListener('click', () => {
        this.loadData();
    });

    this.querySelector('#user-details-modal')?.addEventListener('close', () => {
        this._selectedIdentity = null;
    });

    const saveBar = this.querySelector('app-save-bar') as AppSaveBar;

    if (this._tab === 'settings' && saveBar) {
        // Initialize save bar state when rendering settings tab
        setTimeout(() => saveBar.resetInitialState(this), 0);
        
        const toggles = ['#toggle-non-verified', '#toggle-magic-links', '#toggle-passwords'];
        toggles.forEach(sel => {
            this.querySelector(sel)?.addEventListener('change', () => {
                saveBar.checkDirty(this);
            });
        });

        saveBar.addEventListener('save', () => this.saveSettings());
        saveBar.addEventListener('cancel', () => {
            this.render();
        });
    }
  }
}

if (!customElements.get('app-identity')) {
  customElements.define('app-identity', AppIdentity);
}
